import { useEffect, useRef, useCallback } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';

/**
 * Hook for managing real-time question discussion updates.
 * Subscribes to a specific question channel and handles message/typing events.
 *
 * @param {string} questionId - The question ID to subscribe to
 * @param {Function} onMessageAdded - Callback when a new message is added (message)
 * @returns {Object} Question discussion WebSocket utilities
 */
export function useQuestionDiscussion(questionId, onMessageAdded) {
  const ws = useWebSocket();
  const typingTimeoutRef = useRef(null);

  // Subscribe to question channel
  useEffect(() => {
    if (!ws.isConnected || !questionId) return;

    const channel = `question:${questionId}`;
    ws.subscribe(channel);

    return () => {
      ws.unsubscribe(channel);
    };
    // ws.subscribe/unsubscribe use refs internally and are safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.isConnected, questionId]);

  // Register message handler
  useEffect(() => {
    if (!onMessageAdded) return;

    const handleMessageAdded = (data) => {
      if (data.question_id === questionId && data.message) {
        onMessageAdded(data.message);
      }
    };

    const cleanup = ws.on('question_message_added', handleMessageAdded);

    return () => {
      cleanup();
    };
    // ws.on uses refs internally and is safe to omit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionId, onMessageAdded]);

  // Send typing indicator (debounced)
  const sendTyping = useCallback((isTyping) => {
    if (!questionId) return;

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }

    // Send typing event
    ws.send({
      type: 'question_typing',
      question_id: questionId,
      is_typing: isTyping
    });

    // If starting to type, automatically send "stopped typing" after 3 seconds
    if (isTyping) {
      typingTimeoutRef.current = setTimeout(() => {
        ws.send({
          type: 'question_typing',
          question_id: questionId,
          is_typing: false
        });
      }, 3000);
    }
  }, [questionId, ws]);

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  return {
    isConnected: ws.isConnected,
    sendTyping,
    on: ws.on,
  };
}
